import type { ResolutionEnumNoPrefix } from '@talos/kyoko';
import {
  EMPTY_ARRAY,
  Flex,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  isMultileg,
  SymbolDisplay,
  SymbolDisplaySize,
  useSecuritiesContext,
  useSecurity,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-enterprise';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCombinedMarketsAndAccounts } from '../../../../hooks/useCombinedMarketsAndAccounts';
import { useExchangeMarkets } from '../../../../hooks/useMarketsOfType';
import type { HistoricalPriceChartPanelConfig, SingleSymbolPanelConfig } from '../../../../types/LayoutConfig';
import { Panel } from '../../../Layout/Components/Panel';
import { OHLCVChart } from '../../../OHLCVChart/OHLCVChart';
import { SelectedMarketsDisplay } from '../../../SelectedMarketsDisplay/SelectedMarketsDisplay';
import { SingleSymbolPanelSettings } from './singleSymbol/SingleSymbolPanelSettings';

export function HistoricalPriceChartPanel({ api, params }: IDockviewPanelProps<HistoricalPriceChartPanelConfig>) {
  const [showSettings, setShowSettings] = useState(false);
  const { searchableSecurities, securitiesBySymbol } = useSecuritiesContext();
  const {
    aggregation,
    symbol,
    chartResolution,
    marketAccounts,
    markets,
    enableUnifiedLiquidity,
    customTitle,
    settingsInitialOpen,
    orderPresetID,
    annotations,
    chartType,
  } = params;

  const security = useSecurity(symbol);
  const selectedMarkets = markets ?? EMPTY_ARRAY;
  const selectedMarketAccounts = marketAccounts ?? EMPTY_ARRAY;

  const selectedCombined = useCombinedMarketsAndAccounts(selectedMarketAccounts, selectedMarkets);

  const selectedExchangeMarkets = useExchangeMarkets(selectedCombined, false);
  const securityExchangeMarkets = useExchangeMarkets(security?.Markets ?? EMPTY_ARRAY, true);

  // Open settings panel when the panel is first added and `settingsInitialOpen` is true
  useEffect(() => {
    if (settingsInitialOpen) {
      setShowSettings(true);
      api.updateParameters({ settingsInitialOpen: false });
    }
  }, [settingsInitialOpen, api]);

  const handleChartResolutionChange = useCallback(
    (chartResolution: ResolutionEnumNoPrefix) => {
      api.updateParameters({ chartResolution });
    },
    [api]
  );

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      if (!customTitle) {
        api.setTitle(`${symbol ? (securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol) : 'Trading View'} Chart`);
      }
      api.updateParameters({ symbol, marketAccounts: [], markets: [], aggregation: undefined });
    },
    [api, securitiesBySymbol, customTitle]
  );

  const handleUpdateParameters = useCallback(
    (params: Partial<SingleSymbolPanelConfig>) => {
      api.updateParameters(params);
    },
    [api]
  );

  const handleAnnotationsChange = useCallback(
    (annotations: AgAnnotation[] | undefined) => {
      api.updateParameters({ annotations });
    },
    [api]
  );

  const handleChartTypeChange = useCallback(
    (chartType: AgPriceVolumeChartType) => {
      api.updateParameters({ chartType });
    },
    [api]
  );

  const requestedMarkets = selectedExchangeMarkets.length > 0 ? selectedExchangeMarkets : securityExchangeMarkets;

  const availableSecurities = useMemo(
    // There's a different deepDive for multilegs
    () => searchableSecurities?.filter(s => !isMultileg(s)),
    [searchableSecurities]
  );

  return (
    <>
      <Panel
        panelApi={api}
        position="relative"
        data-testid="historical-price-chart-panel"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {showSettings ? (
          <>
            <HStack h={41} flex="0 0 auto" />
            <SingleSymbolPanelSettings
              onClose={() => setShowSettings(false)}
              availableSecurities={availableSecurities}
              onUpdateParameters={handleUpdateParameters}
              onSymbolChange={handleSymbolChange}
              orderPresetID={orderPresetID}
              marketAccounts={marketAccounts}
              markets={markets}
              aggregation={aggregation}
              symbol={symbol}
              enableUnifiedLiquidity={enableUnifiedLiquidity}
              allowMultileg={true}
              showPresets={false}
              shoeMarketAccounts={false}
            />
          </>
        ) : (
          security && (
            <OHLCVChart
              key={security.Symbol}
              symbol={security.Symbol}
              markets={requestedMarkets}
              resolution={chartResolution ?? '1H'}
              annotations={annotations}
              chartType={chartType ?? 'candlestick'}
              onSymbolChange={handleSymbolChange}
              onResolutionChange={handleChartResolutionChange}
              onAnnotationsChange={handleAnnotationsChange}
              onChartTypeChange={handleChartTypeChange}
              rightAlignedItems={
                <HStack mx="spacing8">
                  <IconButton
                    onClick={() => setShowSettings(!showSettings)}
                    icon={showSettings ? IconName.Close : IconName.Cog}
                    size={FormControlSizes.Small}
                    ghost
                  />
                </HStack>
              }
              leftAlignedItems={
                <SelectedMarketsDisplay
                  aggregation={aggregation}
                  markets={markets}
                  marketAccounts={marketAccounts}
                  tooltip="With no selected exchanges, market data for all exchanges will be used"
                />
              }
            />
          )
        )}
        {showSettings && (
          <HStack
            gap="spacing4"
            justifyContent="space-between"
            position="absolute"
            left="0"
            right="0"
            top="0"
            h={40}
            pr="spacing8"
            pl="spacing12"
            ml={showSettings ? 0 : 156}
            overflow="hidden"
            borderBottom="solid 1px"
            borderColor="backgroundBody"
          >
            <Flex gap="spacing12">
              <SymbolDisplay
                color="colorTextImportant"
                symbol={symbol}
                showDescription={false}
                p="0"
                size={SymbolDisplaySize.Medium}
              />
              <SelectedMarketsDisplay
                aggregation={aggregation}
                markets={markets}
                marketAccounts={marketAccounts}
                tooltip="With no selected exchanges, market data for all exchanges will be used"
              />
            </Flex>
            {showSettings ? (
              <IconButton
                onClick={() => setShowSettings(!showSettings)}
                icon={IconName.Close}
                size={FormControlSizes.Small}
                ghost
              />
            ) : null}
          </HStack>
        )}
      </Panel>
    </>
  );
}
